import { useMemo, useState } from 'react';
import { format, isEqual } from 'date-fns';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { ExpandedState, SortingState } from '@tanstack/react-table';
import {
  useAuthUserRestaurant,
  useGetInvoicingReportQuery,
} from '@durma-soft/gros-sdk';

import { useDatePickerRangeState } from '@/hooks/use-date-picker-range-state';

import { PrintReportButton } from '@/components/shared/print-report-button';
import { Skeleton } from '@/components/shared/shadcn-ui/skeleton';
import { LastUpdatedInfo } from '@/components/shared/last-updated-info';
import { DataTable, DataTableHTML } from '@/components/shared/data-table';
import { DatePickerWithRange } from '@/components/shared/date-picker-with-range';

import print from '@/lib/print/print';

import { repeat } from '@/utils/helpers';

import { columns } from '@/config/restaurant/reports/invoicing';

const InvoicingReport = () => {
  const { t } = useTranslation();

  const memoizedColumns = useMemo(() => columns(t), [t]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [expanded, setExpanded] = useState<ExpandedState>({});

  const restaurant = useAuthUserRestaurant();
  const { from, to, setDateRange } = useDatePickerRangeState();

  const { data, isLoading, isSuccess, dataUpdatedAt, isRefetching, refetch } =
    useGetInvoicingReportQuery({
      restaurant_id: restaurant.id,
      start_date: from,
      end_date: to,
    });

  const handleRefetch = async () => {
    await refetch();
  };

  return (
    <>
      <Helmet>
        <title>{t('reports.invoicing.title')}</title>
      </Helmet>
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold md:text-2xl">
          {t('reports.invoicing.title')}
        </h1>
        {isLoading && <Skeleton className="h-10 w-52" />}
        {isSuccess && (
          <LastUpdatedInfo
            dataUpdatedAt={dataUpdatedAt}
            isRefetching={isRefetching}
            handleRefetch={handleRefetch}
          />
        )}
      </div>
      <div className="flex flex-col lg:flex-row gap-5 mt-5 mb-5">
        <div>
          <DatePickerWithRange
            date={{
              from: new Date(from),
              to: new Date(to),
            }}
            setDate={([from, to]) => {
              setDateRange({ from, to });
            }}
          />
        </div>
        <div className="flex-1 justify-start lg:justify-end flex">
          <PrintReportButton
            disabled={isLoading}
            onPrint={(fontScale) =>
              print({
                fontScale,
                content: (
                  <div>
                    <div>
                      <div className="flex-between">
                        <h1>{t('reports.invoicing.title')}</h1>
                        <div className="flex-row">
                          <p>{t('common.data-updated-at')}</p>
                          <span>&nbsp;</span>
                          <p>
                            <span> {format(dataUpdatedAt, 'dd.MM.yyyy')}</span>
                            <span> {t('common.in')} </span>
                            <span> {format(dataUpdatedAt, 'HH:mm:ss')}</span>
                          </p>
                        </div>
                      </div>
                      <div className="flex-between">
                        <p>
                          {format(from, 'dd.MM.yyyy')}
                          {!isEqual(from, to) &&
                            ` - ${format(to, 'dd.MM.yyyy')}`}
                        </p>
                      </div>
                    </div>
                    <DataTableHTML
                      data={data}
                      sorting={sorting}
                      setSorting={setSorting}
                      columns={memoizedColumns}
                      subrowsKey="locations"
                    />
                  </div>
                ),
              })
            }
          />
        </div>
      </div>
      <div>
        {isLoading && (
          <div className="flex flex-col gap-10">
            {repeat(3).map((index) => (
              <div key={index}>
                <Skeleton className="w-full rounded-md h-60" />
              </div>
            ))}
          </div>
        )}
        {isSuccess && (
          <DataTable
            data={data}
            sorting={sorting}
            setSorting={setSorting}
            columns={memoizedColumns}
            subrowsKey="locations"
            expanded={expanded}
            setExpanded={setExpanded}
            getRowDepthClassName={(depth) => {
              if (depth === 0) {
                return 'bg-muted hover:bg-muted';
              }
            }}
          />
        )}
      </div>
    </>
  );
};

export default InvoicingReport;
